import { Checkbox } from '@rocco/components/checkbox';
import {
    GateFieldLabel,
    GateInput,
    GateSelect,
    GateTextarea,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { FileAttachment } from '~/modules/enquiry/component/FileAttachment';
import { StepHeading } from './StepText';

/** Per-file size cap (bytes). Tighter than the default enquiry cap because
 *  each Get Matched email rolls all attachments into Mandrill's 25MB total. */
const MAX_FILE_SIZE = 4 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;

const MAX_DESCRIPTION_LENGTH = 5000;

interface Step6ProjectValue {
    description: string;
    professionals: string | null;
    startDate: string | null;
    endDate: string | null;
    attachments: File[];
}

interface Step6ProjectProps {
    title: string;
    /** Label + options for the "Which X are you looking for" select. Null hides the field entirely. */
    lookingForField: {
        label: string;
        options: string[];
    } | null;
    /** Project flow drops the timeline question. */
    showTimeline: boolean;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    timelineLabel: string;
    timelineStartLabel: string;
    timelineEndLabel: string;
    value: Step6ProjectValue;
    onChange: (next: Step6ProjectValue) => void;
}

/**
 * The free-text step of the brief, in the gate's own field language
 * (GateFormControls) so it reads as the same form family as the enquiry gate
 * and the auth steps around it.
 */
export const Step6Project = ({
    title,
    lookingForField,
    showTimeline,
    descriptionLabel,
    descriptionPlaceholder,
    timelineLabel,
    timelineStartLabel,
    timelineEndLabel,
    value,
    onChange,
}: Step6ProjectProps) => {
    return (
        <div className="flex flex-col gap-[1.375rem]">
            <StepHeading>{title}</StepHeading>
            <div className="flex flex-col gap-[1.375rem]">
                <Field label={descriptionLabel}>
                    <GateTextarea
                        value={value.description}
                        onChange={(e) =>
                            onChange({ ...value, description: e.target.value })
                        }
                        placeholder={descriptionPlaceholder}
                        rows={7}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                    />
                    <DescriptionCounter length={value.description.length} />
                </Field>
                {lookingForField && (
                    <Field label={lookingForField.label}>
                        <GateSelect
                            value={value.professionals ?? ''}
                            onChange={(e) =>
                                onChange({
                                    ...value,
                                    professionals: e.target.value,
                                })
                            }
                        >
                            <option value="" disabled>
                                Select
                            </option>
                            {lookingForField.options.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </GateSelect>
                    </Field>
                )}
                {showTimeline && (
                    <Field label={timelineLabel}>
                        <ProjectTimeline
                            startDate={value.startDate}
                            endDate={value.endDate}
                            startLabel={timelineStartLabel}
                            endLabel={timelineEndLabel}
                            onChange={({ startDate, endDate }) =>
                                onChange({ ...value, startDate, endDate })
                            }
                        />
                    </Field>
                )}
                <Field
                    label={`Attach files (max ${MAX_ATTACHMENTS}, ${MAX_FILE_SIZE / (1024 * 1024)}MB each)`}
                >
                    <FileAttachment
                        variant="compact"
                        files={value.attachments}
                        maxAttachments={MAX_ATTACHMENTS}
                        maxFileSize={MAX_FILE_SIZE}
                        onChange={(attachments) =>
                            onChange({ ...value, attachments })
                        }
                    />
                </Field>
            </div>
        </div>
    );
};

const Field = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className="flex flex-col gap-1.5">
        <GateFieldLabel>{label}</GateFieldLabel>
        {children}
    </div>
);

const DescriptionCounter = ({ length }: { length: number }) => {
    const ratio = length / MAX_DESCRIPTION_LENGTH;
    const tone =
        ratio >= 1
            ? 'text-red-600'
            : ratio >= 0.9
              ? 'text-[#a35a00]'
              : 'text-[#858585]';
    return (
        <span className={`self-end text-[0.7rem] tracking-[-0.01em] ${tone}`}>
            {length} / {MAX_DESCRIPTION_LENGTH}
        </span>
    );
};

/**
 * Date-range picker matching BuildHub's ProjectTimelineStep shape. "I'm not
 * sure yet" nulls both dates so the brief can be submitted without committing
 * to a timeline.
 */
const ProjectTimeline = ({
    startDate,
    endDate,
    startLabel,
    endLabel,
    onChange,
}: {
    startDate: string | null;
    endDate: string | null;
    startLabel: string;
    endLabel: string;
    onChange: (next: {
        startDate: string | null;
        endDate: string | null;
    }) => void;
}) => {
    const notSure = startDate === null && endDate === null;
    return (
        <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
                <DateInput
                    label={startLabel}
                    value={startDate ?? ''}
                    onChange={(next) =>
                        onChange({ startDate: next || null, endDate })
                    }
                />
                <DateInput
                    label={endLabel}
                    value={endDate ?? ''}
                    onChange={(next) =>
                        onChange({ startDate, endDate: next || null })
                    }
                />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-[0.75rem] tracking-[-0.01em] text-[#161616]">
                <Checkbox
                    checked={notSure}
                    onCheckedChange={(checked) => {
                        if (checked === true) {
                            onChange({ startDate: null, endDate: null });
                        }
                    }}
                />
                I&apos;m not sure yet
            </label>
        </div>
    );
};

const DateInput = ({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
}) => (
    <label className="flex flex-col gap-1">
        <span className="text-[0.7rem] font-medium tracking-[-0.01em] text-[#858585]">
            {label}
        </span>
        <GateInput
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    </label>
);
